import { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  PageContainer,
  Button,
  DataTable,
  Skeleton,
  ErrorBoundary,
  StockLevelIndicator,
} from '@bbpos/ui';
import type { ColumnDef } from '@bbpos/ui';
import { productService } from '@/services/product.service';
import { locationService } from '@/services/location.service';
import { useDashboardStore } from '@/stores/use-dashboard-store';
import type { Product } from '@/types/product';

// A blank Min Stock (null, C04) is never on this list, so every row has a number.
type LowStockRow = Product & { id: string; low_stock_threshold: number };

/**
 * Resolve the "Most Affected" category for the KPI card. Prefer the dataset-wide
 * count the API returns in `meta.topCategory` (issue #226) so the card names the
 * category with the most alerts across the whole set and stays stable across
 * pagination. Only fall back to a current-page tally when meta is unavailable.
 */
export function computeTopCategory(
  metaTop: { category: string; count: number } | null | undefined,
  pageRows: { category?: string | null }[],
): [string, number] | undefined {
  if (metaTop) return [metaTop.category, metaTop.count];
  const catCounts = new Map<string, number>();
  pageRows.forEach((p) => {
    const cat = p.category ?? 'Uncategorized';
    catCounts.set(cat, (catCounts.get(cat) ?? 0) + 1);
  });
  return Array.from(catCounts.entries()).sort((a, b) => b[1] - a[1])[0];
}

/** What a low-stock row carries into the PO wizard. */
export interface PoPrefill {
  id: string;
  name: string;
  sku?: string;
  quantity?: number;
}

/**
 * Quantity to order for a low-stock row: back up to twice the threshold, at
 * least 1. The same rule Auto-Generate PO uses (purchase-orders.repository
 * autoGenerate), so both ways of ordering agree. Stock is NUMERIC and can
 * arrive as a string; rounded to 3 places like stock quantities.
 */
export function bulkOrderQty(stock: number | string, threshold: number | string): number {
  return Number(Math.max(Number(threshold) * 2 - Number(stock), 1).toFixed(3));
}

/** The PO wizard prefill for one low-stock row. */
function prefillFor(row: LowStockRow): PoPrefill {
  return { id: row.id, name: row.name, sku: row.sku, quantity: bulkOrderQty(row.stock_quantity, row.low_stock_threshold) };
}

/**
 * Build the URL that opens the Purchase Order creation flow. Each low-stock
 * product passed — one from a row "Create PO", or every product ticked for the
 * header "Bulk Create PO" (QA 6.8.15) — is carried as repeated
 * product_id/product_name/sku/quantity query params so the wizard opens with
 * one line per product. sku and quantity are always appended (empty when
 * unknown) because the wizard reads them by position. With no products it
 * opens a blank order. See GitHub issue #228.
 */
export function buildCreatePoUrl(products?: PoPrefill | PoPrefill[]): string {
  const base = '/inventory/purchase-orders/new';
  const list = products === undefined ? [] : Array.isArray(products) ? products : [products];
  if (list.length === 0) return base;
  const params = new URLSearchParams();
  list.forEach((p) => {
    params.append('product_id', p.id);
    params.append('product_name', p.name);
    params.append('sku', p.sku ?? '');
    params.append('quantity', p.quantity === undefined ? '' : String(p.quantity));
  });
  return `${base}?${params.toString()}`;
}

// ─── KPI Card ────────────────────────────────────────────────────────────────

interface KpiConfig {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  gradient: string;
  iconBg: string;
  tintBg: string;
  darkTintBg: string;
}

function KpiCard({ title, value, icon, gradient, iconBg, tintBg, darkTintBg }: KpiConfig) {
  return (
    <div className={`relative overflow-hidden rounded-xl ${tintBg} ${darkTintBg} shadow-md hover:shadow-lg transition-all duration-200 p-5 group animate-slide-up`}>
      <div className={`absolute -top-6 -right-6 w-20 h-20 rounded-full ${gradient} opacity-20 group-hover:opacity-30 transition-opacity duration-300`} />
      <div className="relative flex items-center gap-3 mb-3">
        <div className={`w-10 h-10 rounded-lg ${iconBg} flex items-center justify-center text-white shadow-sm`}>
          {icon}
        </div>
        <span className="text-sm font-medium text-neutral-500 dark:text-neutral-400">{title}</span>
      </div>
      <div className="relative">
        <p className="text-2xl font-bold text-neutral-900 dark:text-white tabular-nums">{value}</p>
      </div>
    </div>
  );
}

// ─── Table Columns ───────────────────────────────────────────────────────────

const columns: ColumnDef<LowStockRow>[] = [
  {
    key: 'name',
    header: 'Product',
    render: (_v, row) => (
      <div className="flex items-center gap-3">
        {row.image_url ? (
          <img src={row.image_url} alt={row.name} className="h-9 w-9 rounded-lg object-cover bg-neutral-100 dark:bg-neutral-700" />
        ) : (
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-neutral-100 dark:bg-neutral-700 text-neutral-400">
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
            </svg>
          </div>
        )}
        <div>
          <p className="text-sm font-medium text-neutral-900 dark:text-neutral-100">{row.name}</p>
          <p className="text-xs text-neutral-400 dark:text-neutral-500">{row.category ?? '\u2014'}</p>
        </div>
      </div>
    ),
  },
  {
    key: 'sku',
    header: 'SKU',
    render: (_v, row) => <span className="text-sm text-neutral-600 dark:text-neutral-300">{row.sku}</span>,
  },
  {
    key: 'stock_quantity',
    header: 'Current Stock',
    numeric: true,
    render: (_v, row) => (
      <span className="text-sm font-medium text-neutral-900 dark:text-neutral-100 tabular-nums">
        {Number(row.stock_quantity).toLocaleString('en-IN')} {row.unit}
      </span>
    ),
  },
  {
    key: 'low_stock_threshold',
    header: 'Threshold',
    numeric: true,
    render: (_v, row) => (
      <span className="text-sm text-neutral-500 dark:text-neutral-400 tabular-nums">{row.low_stock_threshold} {row.unit}</span>
    ),
  },
  {
    key: 'status',
    header: 'Status',
    render: (_v, row) => (
      <StockLevelIndicator
        currentStock={Number(row.stock_quantity)}
        reorderLevel={row.low_stock_threshold}
        variant="badge"
        showQuantity
        size="sm"
      />
    ),
  },
];

function LowStockAlertsContent() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const selectedLocationId = useDashboardStore((s) => s.selectedLocationId);

  const page = Number(searchParams.get('page') ?? '1');
  const oosOnly = searchParams.get('filter') === 'out-of-stock';
  // Ticked rows for "Bulk Create PO", kept as id → prefill so a selection
  // survives paging to rows that are no longer loaded.
  const [selected, setSelected] = useState<Map<string, PoPrefill>>(new Map());

  const { data, isLoading, error } = useQuery({
    queryKey: ['low-stock', selectedLocationId, page],
    queryFn: () =>
      productService.getLowStock({
        location_id: selectedLocationId,
        page,
        limit: 20,
      }),
    staleTime: 15_000,
  });

  // Out-of-stock drill-down: filter on the server with the same query the
  // dashboard's Out of Stock figure counts from, so rows and count cover the
  // whole dataset rather than whatever zero-stock rows landed on this page.
  const { data: oosData, isLoading: oosLoading, error: oosError } = useQuery({
    queryKey: ['low-stock-oos', selectedLocationId, page],
    queryFn: () =>
      productService.list({
        location_id: selectedLocationId,
        stock_status: 'out_of_stock',
        page,
        limit: 20,
      }),
    enabled: oosOnly,
    staleTime: 15_000,
  });

  // With "All locations" each location's row of a SKU is listed separately, so
  // a Location column tells the rows apart (and which one is being ordered).
  const allLocations = !selectedLocationId;
  const { data: locationsData } = useQuery({
    queryKey: ['locations-list-all'],
    queryFn: () => locationService.list({ limit: 100 }),
    enabled: allLocations,
    staleTime: 60_000,
  });
  const locationNames = new Map((locationsData?.data ?? []).map((l) => [l.id, l.name]));

  // Sort: OOS first, then by how far below threshold
  const sortedProducts: LowStockRow[] = [...((data?.data ?? []) as LowStockRow[])].sort((a, b) => {
    const aOos = a.stock_quantity <= 0 ? 0 : 1;
    const bOos = b.stock_quantity <= 0 ? 0 : 1;
    if (aOos !== bOos) return aOos - bOos;
    const aPct = a.low_stock_threshold > 0 ? a.stock_quantity / a.low_stock_threshold : 1;
    const bPct = b.low_stock_threshold > 0 ? b.stock_quantity / b.low_stock_threshold : 1;
    return aPct - bPct;
  }) as LowStockRow[];

  // When arriving from the "Out of Stock" KPI (issue #219), show the server-filtered rows.
  const visibleProducts = oosOnly
    ? ((oosData?.data ?? []) as LowStockRow[])
    : sortedProducts;
  const visibleTotal = oosOnly ? (oosData?.meta?.total ?? visibleProducts.length) : undefined;

  // Summary stats — KPI counts come from dataset-wide meta (issue #225), not the
  // current page of ≤20 rows, so they reflect the full alert set for the location
  // and stay stable as the user paginates.
  const allProducts = (data?.data ?? []) as LowStockRow[];
  const meta = data?.meta as
    | {
        total: number;
        outOfStock?: number;
        lowStock?: number;
        topCategory?: { category: string; count: number } | null;
      }
    | undefined;
  const totalAlerts = meta?.total ?? allProducts.length;
  const oosCount = meta?.outOfStock ?? allProducts.filter((p) => p.stock_quantity <= 0).length;
  const lowCount =
    meta?.lowStock ??
    allProducts.filter((p) => p.stock_quantity > 0 && p.stock_quantity <= p.low_stock_threshold).length;

  // Most affected category — computed dataset-wide by the API (issue #226), so it
  // names the category with the most alerts across the whole set and stays stable
  // as the user paginates. Fall back to a current-page tally only if meta is absent.
  const topCategory = computeTopCategory(meta?.topCategory, allProducts);

  if (error || (oosOnly && oosError)) {
    return (
      <div className="rounded-xl bg-danger-50 dark:bg-danger-900/20 shadow-md p-4 text-sm text-danger-700 dark:text-danger-300">
        Failed to load low stock data. Please try refreshing.
      </div>
    );
  }

  if (isLoading || (oosOnly && oosLoading)) {
    return (
      <>
        <Skeleton variant="rect" height={96} className="rounded-xl mb-6" />
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 lg:gap-6 mb-6">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} variant="rect" height={120} className="rounded-xl" />
          ))}
        </div>
        <div className="space-y-3">
          {Array.from({ length: 10 }).map((_, i) => (
            <Skeleton key={i} variant="rect" height={56} />
          ))}
        </div>
      </>
    );
  }

  return (
    <>
      {/* Gradient header */}
      <div className="bg-gradient-to-r from-warning-600 to-danger-600 rounded-xl p-6 text-white shadow-lg mb-6 animate-fade-in">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">{oosOnly ? 'Out of Stock' : 'Low Stock Alerts'}</h1>
            <p className="text-warning-100 mt-1">
              {oosOnly ? 'Products with zero stock on hand' : 'Products below their reorder threshold'}
            </p>
          </div>
          <Button
            variant="secondary"
            onClick={() => navigate(buildCreatePoUrl(Array.from(selected.values())))}
            disabled={selected.size === 0}
            title={selected.size === 0 ? 'Select products to order' : undefined}
            className="bg-white/20 hover:bg-white/30 text-white border-white/30"
          >
            Bulk Create PO{selected.size > 0 ? ` (${selected.size})` : ''}
          </Button>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 lg:gap-6 mb-6">
        <KpiCard
          title="Out of Stock"
          value={oosCount}
          gradient="bg-gradient-to-br from-danger-500 to-danger-700"
          iconBg="bg-gradient-to-br from-danger-500 to-danger-600"
          tintBg="bg-danger-50"
          darkTintBg="dark:bg-danger-900/20"
          icon={<svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" /></svg>}
        />
        <KpiCard
          title="Low Stock Items"
          value={lowCount}
          gradient="bg-gradient-to-br from-warning-500 to-warning-700"
          iconBg="bg-gradient-to-br from-warning-500 to-warning-600"
          tintBg="bg-warning-50"
          darkTintBg="dark:bg-warning-900/20"
          icon={<svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>}
        />
        <KpiCard
          title="Most Affected"
          value={topCategory ? `${topCategory[0]} (${topCategory[1]})` : '\u2014'}
          gradient="bg-gradient-to-br from-teal-500 to-teal-700"
          iconBg="bg-gradient-to-br from-teal-500 to-teal-600"
          tintBg="bg-teal-50"
          darkTintBg="dark:bg-teal-900/20"
          icon={<svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" /></svg>}
        />
      </div>

      {/* Table */}
      <div className="animate-slide-up">
        <DataTable<LowStockRow>
          columns={[
            ...columns.slice(0, 2),
            ...(allLocations
              ? [
                  {
                    key: 'location_id',
                    header: 'Location',
                    render: (_v: unknown, row: LowStockRow) => (
                      <span className="text-sm text-neutral-600 dark:text-neutral-300">
                        {(row.location_id && locationNames.get(row.location_id)) || '—'}
                      </span>
                    ),
                  },
                ]
              : []),
            ...columns.slice(2),
            {
              key: 'action',
              header: 'Action',
              render: (_v, row) => (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(buildCreatePoUrl(prefillFor(row)));
                  }}
                >
                  Create PO
                </Button>
              ),
            },
          ]}
          data={visibleProducts}
          selectable
          selectedRows={Array.from(selected.keys())}
          onSelectionChange={(ids) => {
            const next = new Map<string, PoPrefill>();
            ids.forEach((id) => {
              const row = visibleProducts.find((p) => p.id === id);
              const prefill = selected.get(id) ?? (row ? prefillFor(row) : undefined);
              if (prefill) next.set(id, prefill);
            });
            setSelected(next);
          }}
          loading={isLoading}
          totalCount={visibleTotal ?? totalAlerts}
          currentPage={page}
          pageSize={20}
          onPageChange={(p) => {
            const params = new URLSearchParams(searchParams);
            params.set('page', String(p));
            setSearchParams(params);
          }}
          emptyState={{
            icon: (
              <svg className="h-12 w-12 text-success-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            ),
            title: oosOnly ? 'Nothing out of stock' : 'All stock levels healthy',
            description: oosOnly
              ? 'No products are currently at zero stock'
              : 'No products below their reorder threshold',
          }}
        />
      </div>
    </>
  );
}

export function LowStockAlertsPage() {
  return (
    <PageContainer
      title="Low Stock Alerts"
      subtitle="Products below their reorder threshold"
      breadcrumbs={[{ label: 'Inventory', href: '/inventory' }, { label: 'Low Stock' }]}
    >
      <ErrorBoundary resetKey="low-stock-alerts">
        <LowStockAlertsContent />
      </ErrorBoundary>
    </PageContainer>
  );
}
